import React, { useState, useEffect, useRef, useMemo } from "react";
import { Spin, message } from "antd";
import { BookOpen } from "lucide-react";
import {
  _get_ambassador_lessons,
  _apply_as_ambassador,
  _get_ambassador_status,
  _update_ambassador_lesson_progress,
} from "../../../common/axios_services";
import LessonIntroView from "../../../components/ambassador/lessons/LessonIntroView";
import LessonPlayerView from "../../../components/ambassador/lessons/LessonPlayerView";
import LessonQuizView from "../../../components/ambassador/lessons/LessonQuizView";
import LessonScoreView from "../../../components/ambassador/lessons/LessonScoreView";
import LessonUpgradeTier2View from "../../../components/ambassador/lessons/LessonUpgradeTier2View";
import LessonWaitingApprovalView from "../../../components/ambassador/lessons/LessonWaitingApprovalView";

const AmbassadorLessons = ({ onCompleted, userProfile, initialFlowState, onRefreshUser }) => {
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState([]);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [whatsappGroupUrl, setWhatsappGroupUrl] = useState(
    "https://chat.whatsapp.com/GE4hMb6ulTjGj99NekfZVk"
  );

  const userId = userProfile?.id || userProfile?.accountId || "user";
  const isTier2 = userProfile?.tier === "tier_2" || userProfile?.tier === "2" || userProfile?.tier === "tier_3";

  const getInitialState = () => {
    const hasFinishedQuiz =
      Boolean(userProfile?.trainingCompleted) ||
      (Boolean(userProfile?.isAmbassador) && userProfile?.status === "APPROVED");
    if (hasFinishedQuiz) {
      return "WAITING_APPROVAL";
    }
    if (initialFlowState) return initialFlowState;
    return "INTRO";
  };

  const [flowState, setFlowState] = useState(getInitialState);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [serverProgress, setServerProgress] = useState(null);
  const [watchedLessons, setWatchedLessons] = useState(() => {
    let initial = {};
    if (userProfile?.lessonsCompleted) {
      if (typeof userProfile.lessonsCompleted === "object") {
        initial = { ...userProfile.lessonsCompleted };
      } else if (typeof userProfile.lessonsCompleted === "string") {
        try {
          initial = JSON.parse(userProfile.lessonsCompleted);
        } catch (e) {}
      }
    }
    const isApprovedAmb = Boolean(
      userProfile?.isAmbassador ||
      userProfile?.status === "APPROVED" ||
      userProfile?.ambassadorStatus === "APPROVED"
    );
    const targetLessonsCount = Number(userProfile?.totalLessons) || 0;
    if (isApprovedAmb || userProfile?.lessonsCompletedCount || userProfile?.trainingCompleted) {
      const count = (isApprovedAmb || userProfile?.trainingCompleted) ? targetLessonsCount : Number(userProfile.lessonsCompletedCount || 0);
      for (let i = 0; i < count; i++) {
        initial[i] = true;
      }
    }
    const stored = localStorage.getItem(`konnect_ambassador_watched_lessons_${userId}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === "object") {
          initial = { ...initial, ...parsed };
        }
      } catch (e) {}
    }
    return initial;
  });

  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [answersLocked, setAnswersLocked] = useState({});

  const [submittingStatus, setSubmittingStatus] = useState(false);
  const [refreshingTier, setRefreshingTier] = useState(false);

  const iframeRef = useRef(null);

  useEffect(() => {
    if (userProfile?.lessonsCompleted) {
      try {
        const parsed =
          typeof userProfile.lessonsCompleted === "string"
            ? JSON.parse(userProfile.lessonsCompleted)
            : userProfile.lessonsCompleted;
        if (parsed && typeof parsed === "object") {
          setWatchedLessons((prev) => ({
            ...prev,
            ...parsed,
          }));
        }
      } catch (e) {}
    }
    const isApprovedAmb = Boolean(
      userProfile?.isAmbassador ||
      userProfile?.status === "APPROVED" ||
      userProfile?.ambassadorStatus === "APPROVED"
    );
    const targetLessonsCount = lessons.length || Number(userProfile?.totalLessons) || 0;
    if (isApprovedAmb || userProfile?.lessonsCompletedCount || userProfile?.trainingCompleted) {
      const count = (isApprovedAmb || userProfile?.trainingCompleted) ? targetLessonsCount : Number(userProfile.lessonsCompletedCount || 0);
      setWatchedLessons((prev) => {
        const next = { ...prev };
        for (let i = 0; i < count; i++) {
          next[i] = true;
          if (lessons[i]?.id) {
            next[lessons[i].id] = true;
          }
        }
        return next;
      });
    }
  }, [userProfile?.lessonsCompleted, userProfile?.lessonsCompletedCount, userProfile?.trainingCompleted, userProfile?.isAmbassador, userProfile?.status, userProfile?.totalLessons, lessons]);

  useEffect(() => {
    if (initialFlowState) {
      setFlowState(initialFlowState);
    }
  }, [initialFlowState]);

  useEffect(() => {
    if (flowState === "UPGRADE_TIER2") {
      setFlowState("WAITING_APPROVAL");
    }
  }, [flowState]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await _get_ambassador_lessons();
        const data = res.data?.data || res.data || {};
        const fetchedLessons = Array.isArray(data.lessons) ? data.lessons : [];
        const fetchedQuiz = Array.isArray(data.quiz) ? data.quiz : [];

        setLessons(fetchedLessons);
        setQuizQuestions(fetchedQuiz);

        if (data.whatsappGroupUrl) {
          setWhatsappGroupUrl(data.whatsappGroupUrl);
        }

        try {
          const statusRes = await _get_ambassador_status();
          const statusData = statusRes.data?.data || statusRes.data || {};
          setServerProgress(statusData);
          const isFinished = Boolean(
            statusData.trainingCompleted ||
            (statusData.isAmbassador && statusData.status === "APPROVED")
          );
          if (isFinished) {
            if (!initialFlowState) {
              setFlowState((curr) => (curr === "INTRO" ? curr : "WAITING_APPROVAL"));
            }
          } else {
            localStorage.removeItem(`konnect_ambassador_quiz_completed_${userId}`);
            localStorage.removeItem(`konnect_ambassador_quiz_score_${userId}`);
            fetchedLessons.forEach((l, idx) => {
              if (l?.id) {
                localStorage.removeItem(`konnect_ambassador_quiz_completed_${userId}_${l.id}`);
              }
              localStorage.removeItem(`konnect_ambassador_quiz_completed_${userId}_${idx}`);
            });
          }
          let serverWatchedLessons = {};
          if (statusData.lessonsCompleted) {
            const parsed =
              typeof statusData.lessonsCompleted === "string"
                ? JSON.parse(statusData.lessonsCompleted)
                : statusData.lessonsCompleted;
            if (parsed && typeof parsed === "object") {
              serverWatchedLessons = { ...parsed };
            }
          }
          const isApprovedAmb = Boolean(
            statusData.isAmbassador ||
            statusData.status === "APPROVED" ||
            statusData.ambassadorStatus === "APPROVED"
          );
          const isTrainingDone = Boolean(
            isApprovedAmb ||
            statusData.trainingCompleted
          );
          const serverCompletedCount = isTrainingDone ? (fetchedLessons.length || Number(statusData.totalLessons) || 0) : Number(statusData.lessonsCompletedCount || 0);
          for (let i = 0; i < serverCompletedCount; i++) {
            serverWatchedLessons[i] = true;
            if (fetchedLessons[i]?.id) {
              serverWatchedLessons[fetchedLessons[i].id] = true;
            }
          }
          setWatchedLessons(serverWatchedLessons);
          if (statusData.quizScore !== undefined && statusData.quizScore !== null) {
            localStorage.setItem(`konnect_ambassador_quiz_score_${userId}`, String(statusData.quizScore));
          }
        } catch (e) {}
      } catch (err) {
        console.error("Failed to load ambassador lessons:", err);
        setLessons([]);
        setQuizQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isTier2, userId]);

  const handleMarkAsWatched = async (targetLesson) => {
    const lessonToMark = targetLesson || activeLesson;
    if (!lessonToMark) return;

    const updated = {
      ...watchedLessons,
      [lessonToMark.id]: true,
      [currentLessonIndex]: true,
    };
    setWatchedLessons(updated);

    try {
      localStorage.setItem(
        `konnect_ambassador_watched_lessons_${userId}`,
        JSON.stringify(updated)
      );
      const completedCount = Math.max(
        Object.keys(updated).filter((k) => updated[k] && !isNaN(Number(k))).length,
        currentLessonIndex + 1,
        Number(serverProgress?.lessonsCompletedCount ?? userProfile?.lessonsCompletedCount ?? 0)
      );
      const isAllWatched = completedCount >= lessons.length && lessons.length > 0;
      await _update_ambassador_lesson_progress({
        lessonsCompleted: updated,
        lessonsCompletedCount: completedCount,
        trainingCompleted: isAllWatched && (quizQuestions.length === 0 || userProfile?.trainingCompleted),
      });
      if (onRefreshUser) {
        onRefreshUser();
      }

      if (activeLessonQuestions.length > 0 && !isCurrentLessonQuizCompleted) {
        message.success(`Lesson "${lessonToMark.title}" completed! Please attempt the quiz questions.`);
        setFlowState("QUIZ");
        setCurrentQuizIndex(0);
      } else if (currentLessonIndex < lessons.length - 1) {
        message.success(`Lesson "${lessonToMark.title}" completed! You can now start the next lesson.`);
        setCurrentLessonIndex((prev) => prev + 1);
      } else {
        setFlowState("SCORE");
      }
    } catch (e) {
      console.error("Failed to sync watched status to backend:", e);
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      try {
        let data = event.data;
        if (typeof data === "string") {
          data = JSON.parse(data);
        }
        if (data?.event === "onStateChange" && data?.info === 0) {
          const active = lessons[currentLessonIndex];
          if (active) {
            handleMarkAsWatched(active);
          }
        }
      } catch (err) {}
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [lessons, currentLessonIndex, watchedLessons]);

  const activeLesson = lessons[currentLessonIndex] || lessons[0];
  const isCurrentLessonWatched = Boolean(
    activeLesson && watchedLessons[activeLesson.id]
  );

  const activeLessonQuestions = useMemo(() => {
    if (!activeLesson) return [];
    if (activeLesson.quizzes && activeLesson.quizzes.length > 0) {
      return activeLesson.quizzes;
    }
    const filtered = quizQuestions.filter((q) => q.lessonId === activeLesson.id);
    if (filtered.length > 0) return filtered;

    const hasAnyLessonWithAssignedQuiz = quizQuestions.some((q) => Boolean(q.lessonId));
    if (!hasAnyLessonWithAssignedQuiz && quizQuestions.length > 0) {
      const questionsPerLesson = Math.ceil(quizQuestions.length / (lessons.length || 1));
      const start = currentLessonIndex * questionsPerLesson;
      const slice = quizQuestions.slice(start, start + questionsPerLesson);
      if (slice.length > 0) return slice;
      if (currentLessonIndex === lessons.length - 1) return quizQuestions;
    }
    return [];
  }, [activeLesson, quizQuestions, currentLessonIndex, lessons.length]);

  const userScore = serverProgress?.quizScore !== undefined && serverProgress?.quizScore !== null
    ? Number(serverProgress.quizScore)
    : userProfile?.quizScore !== undefined && userProfile?.quizScore !== null
      ? Number(userProfile.quizScore)
    : Number(localStorage.getItem(`konnect_ambassador_quiz_score_${userId}`) || 0);

  const isOverallQuizCompleted = Boolean(
    serverProgress?.trainingCompleted ||
    userProfile?.trainingCompleted ||
    serverProgress?.isAmbassador ||
    userProfile?.isAmbassador ||
    (serverProgress?.status === "APPROVED" && serverProgress?.isAmbassador) ||
    (userProfile?.status === "APPROVED" && userProfile?.isAmbassador)
  );

  const isCurrentLessonQuizCompleted = Boolean(
    isOverallQuizCompleted ||
    (localStorage.getItem(`konnect_ambassador_quiz_completed_${userId}_${activeLesson?.id || currentLessonIndex}`) === "true" &&
     Boolean(watchedLessons[`quiz_${activeLesson?.id}`] || watchedLessons[`quiz_${currentLessonIndex}`]))
  );

  const handleNextLesson = () => {
    if (activeLessonQuestions.length > 0 && !isCurrentLessonQuizCompleted) {
      setFlowState("QUIZ");
      setCurrentQuizIndex(0);
    } else if (currentLessonIndex < lessons.length - 1) {
      setCurrentLessonIndex((prev) => prev + 1);
    } else {
      setFlowState("SCORE");
    }
  };

  const handlePreviousLesson = () => {
    if (currentLessonIndex > 0) {
      setCurrentLessonIndex((prev) => prev - 1);
    } else {
      setFlowState("INTRO");
    }
  };

  const handleSelectQuizAnswer = (questionId, optionIndex) => {
    if (answersLocked[questionId]) return;

    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: optionIndex,
    }));
    setAnswersLocked((prev) => ({
      ...prev,
      [questionId]: true,
    }));
  };

  const handleNextQuestion = async () => {
    const isLastQuestionOfLesson = currentQuizIndex >= activeLessonQuestions.length - 1;
    const isLastLessonOfAll = currentLessonIndex >= lessons.length - 1;
    const isLastQuizOfAll = isLastQuestionOfLesson && isLastLessonOfAll;

    if (!isLastQuestionOfLesson) {
      setCurrentQuizIndex((prev) => prev + 1);
      return;
    }

    const currentScore = calculateScore();
    const quizKey = `quiz_${activeLesson?.id || currentLessonIndex}`;
    const updatedWithQuiz = {
      ...watchedLessons,
      [quizKey]: true,
      ...(isLastQuizOfAll ? { quiz_taken: true, quiz_completed: true } : {}),
    };
    setWatchedLessons(updatedWithQuiz);
    localStorage.setItem(
      `konnect_ambassador_quiz_completed_${userId}_${activeLesson?.id || currentLessonIndex}`,
      "true"
    );
    if (isLastQuizOfAll) {
      localStorage.setItem(`konnect_ambassador_quiz_completed_${userId}`, "true");
    }
    localStorage.setItem(`konnect_ambassador_quiz_score_${userId}`, String(currentScore));

    try {
      await _update_ambassador_lesson_progress({
        quizScore: currentScore,
        lessonsCompleted: updatedWithQuiz,
        trainingCompleted: isLastQuizOfAll,
        status: isLastQuizOfAll ? "WAITING_APPROVAL" : undefined,
      });
      if (onRefreshUser) {
        onRefreshUser();
      }
    } catch (err) {}

    if (!isLastLessonOfAll) {
      const nextIndex = currentLessonIndex + 1;
      const nextLesson = lessons[nextIndex];
      const isNextLessonWatched = Boolean(
        nextLesson && (watchedLessons[nextLesson.id] || watchedLessons[nextIndex])
      );
      const isNextQuizDone = Boolean(
        localStorage.getItem(`konnect_ambassador_quiz_completed_${userId}_${nextLesson?.id || nextIndex}`) === "true" ||
        watchedLessons[`quiz_${nextLesson?.id}`] ||
        watchedLessons[`quiz_${nextIndex}`]
      );

      setCurrentLessonIndex(nextIndex);
      setCurrentQuizIndex(0);

      if (isNextLessonWatched && !isNextQuizDone) {
        setFlowState("QUIZ");
      } else {
        setFlowState("LESSON");
      }
    } else {
      setFlowState("SCORE");
    }
  };

  const calculateScore = () => {
    let score = 0;
    quizQuestions.forEach((q) => {
      if (selectedAnswers[q.id] === q.correctAnswer) {
        score += Number(q.points !== undefined && q.points !== null ? q.points : 2);
      }
    });
    return score;
  };

  const calculateTotalPoints = () => {
    let total = 0;
    quizQuestions.forEach((q) => {
      total += Number(q.points !== undefined && q.points !== null ? q.points : 2);
    });
    return total || (quizQuestions.length * 2) || 1;
  };

  const handleFinalizeAndProceed = async () => {
    setSubmittingStatus(true);
    const hasActiveAnswers = Object.keys(selectedAnswers).length > 0;
    const score = hasActiveAnswers ? calculateScore() : userScore;
    try {
      const updatedWithQuiz = {
        ...watchedLessons,
        quiz_taken: true,
        quiz_completed: true,
      };
      setWatchedLessons(updatedWithQuiz);
      localStorage.setItem(`konnect_ambassador_quiz_completed_${userId}`, "true");
      localStorage.setItem(`konnect_ambassador_quiz_score_${userId}`, String(score));

      await _update_ambassador_lesson_progress({
        trainingCompleted: true,
        quizScore: score,
        lessonsCompleted: updatedWithQuiz,
        lessonsCompletedCount: lessons.length || Number(serverProgress?.totalLessons) || Number(userProfile?.totalLessons) || 0,
        status: "WAITING_APPROVAL",
      });

      if (onCompleted) {
        await onCompleted();
      }
      if (onRefreshUser) {
        await onRefreshUser();
      }

      setFlowState("WAITING_APPROVAL");
    } catch (err) {
      setFlowState("WAITING_APPROVAL");
    } finally {
      setSubmittingStatus(false);
    }
  };

  const handleCheckTierStatus = async () => {
    setRefreshingTier(true);
    try {
      if (onRefreshUser) {
        await onRefreshUser();
      } else if (onCompleted) {
        await onCompleted();
      }

      const ambRes = await _get_ambassador_status();
      const ambData = ambRes.data?.data || ambRes.data || {};
      const currentTier = ambData.tier || userProfile?.tier;

      if (currentTier === "tier_2" || currentTier === "tier_3") {
        await _update_ambassador_lesson_progress({
          trainingCompleted: true,
          status: "WAITING_APPROVAL",
        });
        if (onCompleted) {
          await onCompleted();
        }
        if (onRefreshUser) {
          await onRefreshUser();
        }
        setFlowState("WAITING_APPROVAL");
        message.success("Tier 2 verified! Status updated to Waiting for Approval. Please join the official WhatsApp group.");
      } else {
        message.info("Your wallet is currently Tier 1. Please complete BVN verification to upgrade to Tier 2.");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setRefreshingTier(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 space-y-4">
        <Spin size="large" />
        <p className="font-gilroyBold text-lg text-gray-700">
          Loading Ambassador Lessons...
        </p>
      </div>
    );
  }

  const hasApplied = Boolean(
    serverProgress
      ? (serverProgress.hasApplied || serverProgress.isAmbassador || (serverProgress.status && serverProgress.status !== "NOT_APPLIED") || (serverProgress.ambassadorStatus && serverProgress.ambassadorStatus !== "NOT_APPLIED"))
      : (userProfile?.hasApplied || userProfile?.isAmbassador || (userProfile?.ambassadorStatus && userProfile.ambassadorStatus !== "NOT_APPLIED") || (userProfile?.status && userProfile.status !== "NOT_APPLIED") || (userProfile?.ambassadorProfile?.status && userProfile.ambassadorProfile.status !== "NOT_APPLIED"))
  );

  if (!hasApplied) {
    return (
      <div className="bg-white border border-gray-100 rounded-3xl p-6 sm:p-8 text-center space-y-4 shadow-sm my-6">
        <div className="w-16 h-16 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mx-auto text-2xl font-bold">
          !
        </div>
        <h3 className="font-gilroyBold text-xl text-gray-900 m-0">
          Application & Terms Required
        </h3>
        <p className="font-gilroyMedium text-sm text-gray-600 max-w-md mx-auto m-0">
          Please complete and submit the Ambassador Onboarding Application and accept the terms before accessing the training lessons and quizzes.
        </p>
        <div className="pt-2">
          <button
            type="button"
            onClick={() => {
              if (onCompleted) onCompleted();
              window.location.href = "/become-ambassador";
            }}
            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-gilroyBold text-sm rounded-xl shadow-md border-0 cursor-pointer"
          >
            Go to Application Form
          </button>
        </div>
      </div>
    );
  }

  if (lessons.length === 0) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl p-6 text-center shadow-sm space-y-3">
        <BookOpen className="w-10 h-10 text-gray-400 mx-auto" />
        <h2 className="font-gilroyBold text-base text-gray-900 m-0">No Lessons Available</h2>
        <p className="text-gray-600 text-[13px] m-0">
          There are currently no active ambassador lessons published. Please check back later.
        </p>
      </div>
    );
  }

  const maxLessonCount = lessons.length || Number(serverProgress?.totalLessons) || Number(userProfile?.totalLessons) || 0;
  const isApproved = Boolean(
    userProfile?.isAmbassador ||
    serverProgress?.isAmbassador ||
    userProfile?.status === "APPROVED" ||
    serverProgress?.status === "APPROVED" ||
    userProfile?.ambassadorStatus === "APPROVED" ||
    serverProgress?.ambassadorStatus === "APPROVED"
  );
  const isTrainingDone = Boolean(
    isApproved ||
    serverProgress?.trainingCompleted ||
    userProfile?.trainingCompleted
  );
  const completedLessonsCount = isTrainingDone
    ? maxLessonCount
    : Math.min(
        maxLessonCount,
        Math.max(
          Object.keys(watchedLessons).filter((k) => watchedLessons[k] && !isNaN(Number(k))).length,
          lessons.filter((l, idx) => Boolean(watchedLessons[l.id]) || Boolean(watchedLessons[idx])).length,
          Math.min(maxLessonCount, Number(serverProgress?.lessonsCompletedCount ?? userProfile?.lessonsCompletedCount ?? 0))
        )
      );

  const totalQuizPoints = calculateTotalPoints();
  const hasFinishedQuiz = Boolean(isOverallQuizCompleted || isApproved);

  if (flowState === "INTRO") {
    return (
      <LessonIntroView
        lessons={lessons}
        watchedLessons={watchedLessons}
        lessonsCompletedCount={completedLessonsCount}
        quizScore={userScore}
        totalQuizPoints={totalQuizPoints}
        hasFinishedQuiz={hasFinishedQuiz}
        isTier2={isTier2}
        isApprovedAmbassador={isApproved}
        onSelectLesson={(idx) => {
          setCurrentLessonIndex(idx);
          setFlowState("LESSON");
        }}
        onStart={() => {
          if (hasFinishedQuiz && !isApproved) {
            setFlowState("WAITING_APPROVAL");
            return;
          }
          const firstUncompletedIndex = lessons.findIndex((l, idx) => !watchedLessons[l.id] && !watchedLessons[idx] && completedLessonsCount <= idx);
          const nextIndex = firstUncompletedIndex !== -1 ? firstUncompletedIndex : 0;
          setCurrentLessonIndex(nextIndex);
          setFlowState("LESSON");
        }}
        onProceedToNextStep={() => {
          setFlowState("WAITING_APPROVAL");
        }}
      />
    );
  }

  if (flowState === "LESSON") {
    return (
      <LessonPlayerView
        lesson={activeLesson}
        lessonIndex={currentLessonIndex}
        totalLessons={lessons.length}
        lessons={lessons}
        watchedLessons={watchedLessons}
        lessonsCompletedCount={completedLessonsCount}
        isWatched={isCurrentLessonWatched}
        hasQuiz={activeLessonQuestions.length > 0}
        isQuizCompleted={isCurrentLessonQuizCompleted}
        iframeRef={iframeRef}
        onMarkAsWatched={() => handleMarkAsWatched(activeLesson)}
        onSelectLesson={(idx) => setCurrentLessonIndex(idx)}
        onPrevious={handlePreviousLesson}
        onNext={handleNextLesson}
      />
    );
  }

  if (flowState === "QUIZ") {
    const question = activeLessonQuestions[currentQuizIndex];
    if (!question && !isCurrentLessonQuizCompleted) {
      return (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 text-center shadow-sm space-y-3">
          <h2 className="font-gilroyBold text-base text-gray-900 m-0">No Questions Available</h2>
          <p className="text-gray-600 text-[13px] m-0">
            There are currently no quiz questions configured for this lesson.
          </p>
        </div>
      );
    }

    return (
      <LessonQuizView
        lessonTitle={activeLesson?.title}
        lessonIndex={currentLessonIndex}
        totalLessons={lessons.length}
        question={question}
        currentIndex={currentQuizIndex}
        totalQuestions={activeLessonQuestions.length}
        selectedAnswer={question ? selectedAnswers[question.id] : undefined}
        isAnswered={question ? answersLocked[question.id] !== undefined : true}
        onSelectAnswer={handleSelectQuizAnswer}
        onNext={handleNextQuestion}
        isAlreadyTaken={isCurrentLessonQuizCompleted && Object.keys(answersLocked).length === 0}
        quizScore={userScore}
        onProceed={() => {
          if (currentLessonIndex < lessons.length - 1) {
            setCurrentLessonIndex((prev) => prev + 1);
            setFlowState("LESSON");
            setCurrentQuizIndex(0);
          } else {
            setFlowState("SCORE");
          }
        }}
        onReviewLessons={() => {
          setFlowState("LESSON");
        }}
      />
    );
  }

  if (flowState === "SCORE") {
    const hasActiveAnswers = Object.keys(selectedAnswers).length > 0;
    const displayEarnedScore = hasActiveAnswers ? calculateScore() : userScore;
    return (
      <LessonScoreView
        score={displayEarnedScore}
        totalQuestions={quizQuestions.length || 3}
        earnedPoints={displayEarnedScore}
        totalPoints={calculateTotalPoints()}
        submitting={submittingStatus}
        onProceed={handleFinalizeAndProceed}
      />
    );
  }



  return (
    <LessonWaitingApprovalView
      whatsappGroupUrl={whatsappGroupUrl}
      refreshing={refreshingTier}
      onRefresh={handleCheckTierStatus}
      quizScore={userScore}
      totalPoints={totalQuizPoints}
      onReviewLessons={() => {
        setFlowState("INTRO");
        setCurrentLessonIndex(0);
      }}
    />
  );
};

export default AmbassadorLessons;
